import React, { useState, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Badge from '~/Common-Objects/UI/Badge'
import Button from '~/Common-Objects/UI/Forms/Button/Button'
import RadioTile from '~/Common-Objects/UI/Forms/RadioTile'
import UpsellModal from '~/Common-Objects/UpsellModal'
import { channelTypes } from '~/Classes/conversations'
import AlertDialog from '../../../../components/alertDialog'
import * as styles from './index.module.scss'
import i18n from '~/i18n'
import TierPermissions from '~/Classes/tierPermissions'
import useTierAllowed from '~/Common-Objects/hooks/useTierAllowed'
import UpgradeDrawerTiers from '~/pages/cp/includes/upgrade/upgradeTiers'
import { alterSiteDrawer } from '~/data/actions/siteActions'

export default function AddChannelDialog({ inboxChannels, isOpen, onSubmit, ...rest }) {
    const dispatch = useDispatch()
    const isMounted = useRef(true)
    const account = useSelector((state) => state.accountMaster)
    const [channelType, setChannelType] = useState(channelTypes[0].value)
    const [submitting, setSubmitting] = useState(false)
    const [requiresEmailChan, setRequiresEmailChan] = useState(false)
    const [modal, setModal] = useState(null)
    const [drawer, setDrawer] = useState(false)
    const tierAllowed = useTierAllowed('facebookInboxChannel')

    useEffect(() => {
        const emailChannelsCnt = inboxChannels.filter((c) => c.type === 1 && c.status == 'running').length
        if (emailChannelsCnt < 1) {
            setRequiresEmailChan(true)
        }
        return () => {
            isMounted.current = false
        }
    }, [])

    async function handleSubmit() {
        setSubmitting(true)

        try {
            await onSubmit(channelType)
        } finally {
            // Sometimes we may want to redirect after the onSubmit() function is called, and so the component won't be mounted.
            // We need this conditional to avoid memory leaks.
            if (isMounted.current) {
                setSubmitting(false)
            }
        }
    }

    const upsellFacebook = () => {
        const _modal = (
            <UpsellModal
                onUpgradeClick={showUpgradeDrawer}
                feature={'facebookInboxChannel'}
                requiredTier={tierAllowed.requiredTier.name}
                inline={true}
                onClose={() => setModal(null)}
                onUpgrade={() => setModal(null)}
            />
        )
        setModal(_modal)
    }

    const showUpgradeDrawer = (newPlan) => {
        const _drawer = (
            <UpgradeDrawerTiers
                onUpgrade={() => {
                    setDrawer(false)
                    dispatch(alterSiteDrawer(false))
                }}
                newPlan={newPlan}
                upsell={'facebookInboxChannel'}
                close={() => {
                    setDrawer(false)
                    dispatch(alterSiteDrawer(false))
                }}
            />
        )
        setModal(null)
        dispatch(
            alterSiteDrawer(
                true,
                true,
                'right',
                _drawer,
                true,
                1000,
                '',
                () => {
                    setDrawer(false)
                },
                true
            )
        )
        setDrawer(true)
    }

    const selectedChannel = channelTypes.find((c) => c.value === channelType)
    const needsUpgrade =
        selectedChannel && selectedChannel.tier && !TierPermissions(selectedChannel.tier, account).allowed

    const overrideCta = needsUpgrade ? (
        <Button className='button-upsell button-sml' style={{ marginTop: 18 }} onClick={upsellFacebook}>
            Upgrade to Access
        </Button>
    ) : null

    if (modal) {
        return modal
    }

    if (drawer) return null

    return (
        <AlertDialog
            open={isOpen}
            title={i18n.t('chat:settings.channels.add.header')}
            description={i18n.t('chat:settings.channels.add.subheader')}
            ctaText={overrideCta ? 'Upgrade' : i18n.t('chat:settings.channels.add.button')}
            ctaVariant={overrideCta ? 'upsell' : 'primary'}
            submitting={submitting}
            onCtaClick={overrideCta ? upsellFacebook : handleSubmit}
            // overrideCta={overrideCta}
            {...rest}
        >
            <form
                onSubmit={(e) => {
                    e.preventDefault()
                    handleSubmit()
                }}
            >
                {channelTypes.map((channel) => {
                    if (channel.beta) {
                        if (!account.accountMaster.beta?.[channel.beta]) return null
                    }
                    const { label, value, subheading, type } = channel
                    const alreadyEnabled =
                        channel.value === 1 &&
                        inboxChannels.length > 0 &&
                        inboxChannels.filter((c) => c.type === 1 && c.status === 'running').length > 0
                    const checked = channelType === value
                    const chanDisabled = requiresEmailChan && value != 1
                    return (
                        <RadioTile
                            key={value}
                            disabled={chanDisabled}
                            name='channelType'
                            value={value}
                            checked={checked}
                            className='mar-b12'
                            onChange={(e) => setChannelType(Number(e.target.value))}
                        >
                            <div>
                                <h3 className={styles.radioHeading}>{i18n.t(label)}</h3>
                                <p className={styles.radioSubheading}>{i18n.t(subheading)}</p>
                                {alreadyEnabled && (
                                    <Badge className='mar-t5'>
                                        {i18n.t('chat:settings.channels.add.only1')}
                                    </Badge>
                                )}
                                {chanDisabled && (
                                    <Badge className='mar-t5'>
                                        {i18n.t('chat:settings.channels.add.requiresEmail')}
                                    </Badge>
                                )}
                            </div>
                        </RadioTile>
                    )
                })}
            </form>
        </AlertDialog>
    )
}
